import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getJobLogs,
  listStudentJobs,
} from '../../services/complianceService';
import Alert from '../common/Alert';
import Loader from '../common/Loader';

const LOG_FILES = [
  { value: 'full-job.log', label: 'Full job' },
  { value: 'document-service.log', label: 'Document service' },
  { value: 'student-service.log', label: 'Student service' },
  { value: 'manifest.json', label: 'Manifest' },
  { value: 'trigger.json', label: 'Trigger' },
  { value: 'webhook.json', label: 'Webhook' },
];

function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (['completed', 'success', 'done'].includes(s)) {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  }
  if (['failed', 'error', 'cancelled'].includes(s)) {
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  }
  if (['queued', 'pending', 'initiated'].includes(s)) {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  }
  return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

export default function StudentJobsDashboard() {
  const { id: studentId } = useParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [logFile, setLogFile] = useState('full-job.log');
  const [logText, setLogText] = useState('');
  const [logLoading, setLogLoading] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await listStudentJobs(studentId, 50);
      setJobs(data?.data?.jobs || []);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to load jobs.' });
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    setLoading(true);
    loadJobs();
    const timer = setInterval(loadJobs, 8000);
    return () => clearInterval(timer);
  }, [loadJobs]);

  const openLogs = async (jobId, filename = logFile) => {
    setExpandedId(jobId);
    setLogLoading(true);
    setLogText('');
    try {
      const { data } = await getJobLogs(studentId, jobId, filename);
      setLogText(typeof data === 'string' ? data : String(data ?? ''));
    } catch (err) {
      setLogText(err.response?.data?.detail || 'No logs captured for this job yet.');
    } finally {
      setLogLoading(false);
    }
  };

  if (loading) return <Loader text="Loading jobs..." />;

  return (
    <div className="mx-auto w-full min-w-0 space-y-4">
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to={`/students/${studentId}`}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Upload &amp; Jobs</h1>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
              Job status and logs for this student. Pause / start / restart / stop are on Pipeline Jobs.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/students/${studentId}/files/add`}
            className="px-3 py-2 rounded-xl border border-gray-300 dark:border-slate-700 text-sm font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
          >
            Upload files
          </Link>
          <button
            type="button"
            onClick={() => { setLoading(true); loadJobs(); }}
            className="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden">
        {jobs.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-slate-400">
            No jobs yet for this student. Upload files with compliance enabled to create one.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-slate-700">
            {jobs.map((job, idx) => {
              const jobId = job.job_id;
              const key = jobId || `row-${job.tracking_id || idx}`;
              const pct = job.overall_pct ?? job.progress;
              return (
                <li key={key} className="p-4 sm:p-5">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-3 lg:gap-4">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusTone(job.status)}`}>
                          {job.status || 'unknown'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-slate-400">
                          {job.job_type || 'job'}
                        </span>
                        {jobId && (
                          <span className="text-xs font-mono text-gray-400" title={jobId}>
                            {shortId(jobId)}
                          </span>
                        )}
                        {job.has_logs && (
                          <span className="text-[11px] text-emerald-600 dark:text-emerald-400">logs</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-800 dark:text-slate-100 truncate">
                        {job.title || job.message || job.phase || 'Compliance job'}
                      </p>
                      {job.error && (
                        <p className="text-xs text-red-600 dark:text-red-400 break-words">{job.error}</p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-slate-500">
                        Updated {formatWhen(job.updated_at || job.created_at || job.completed_at || job.captured_at)}
                        {pct != null && pct !== '' ? ` · ${pct}%` : ''}
                        {job.section_name ? ` · ${job.section_name}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                      {jobId && (
                        <button
                          type="button"
                          onClick={() => openLogs(jobId)}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                        >
                          Logs
                        </button>
                      )}
                    </div>
                  </div>

                  {expandedId === jobId && jobId && (
                    <div className="mt-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 overflow-hidden">
                      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-slate-700">
                        <select
                          value={logFile}
                          onChange={(e) => {
                            const next = e.target.value;
                            setLogFile(next);
                            openLogs(jobId, next);
                          }}
                          className="text-xs rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
                        >
                          {LOG_FILES.map((f) => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => openLogs(jobId, logFile)}
                          className="text-xs text-indigo-600 dark:text-indigo-400 font-medium"
                        >
                          Reload
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedId(null)}
                          className="ml-auto text-xs text-gray-500"
                        >
                          Close
                        </button>
                      </div>
                      <pre className="p-3 text-[11px] leading-relaxed max-h-72 overflow-auto whitespace-pre-wrap break-words text-gray-700 dark:text-slate-300">
                        {logLoading ? 'Loading logs…' : (logText || 'Empty log file.')}
                      </pre>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
